
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import * as log from 'loglevel';
  import vSelect from 'vue-select';
  import debounce from 'lodash/debounce';
  import * as StringUtils from '@/utilities/string.utils';
  import {
    IJobItemModel,
    IJobItemActivityModel,
    Client,
    IInvoiceBatchModel,
    ClientSearchResultModel,
    IJobModel,
    CreateInvoiceRequestModel,
    CreateInvoiceRequestLineItemModel,
    InvoiceModel,
    UserModel,
    UserProfileModel,
    IServiceTypeModel
  } from '@/services/api.service';
  import { faLessThanEqual } from '@fortawesome/free-solid-svg-icons';
  import toDateFormatter from '@/filters/todate.formatter';
  import toHoursMinutesFormatter from '@/filters/tohoursminutes.formatter';
  import toCurrencyFormatter from '@/filters/tocurrency.formatter';
  import { SimpleEvent } from '@/modules/ISimpleEvent.ts';
  import NetworkList from '@/views/admin/network/NetworkList.vue';
  import { EventBus } from '@/event-bus';
  import { InvoiceCreatedMessage } from '@/services/eventbus.models';

  export class UninvoicedActivityDetail {
    public gid: string = '';
    public activityDate: Date | undefined;
    public employeeName: string = '';
    public siteTimeInMinutes: number = 0;
    public travelTimeInMinutes: number = 0;
    public description: string = '';

    private internalAccountCode: string = '';
    public get accountCode(): string {
      return this.internalAccountCode;
    }

    public set accountCode(newValue: string) {
      // console.log('Account code changed to ', newValue);
      this.internalAccountCode = newValue;
      this.onAccountCode.trigger();
    }

    private readonly onAccountCode = new SimpleEvent<void>();
    public get onAccountCodeChanged() {
      return this.onAccountCode.expose();
    }

    private internalAmountExTax: number = 0;
    public get amountExTax(): number {
      return this.internalAmountExTax;
    }

    public set amountExTax(newValue: number) {
      this.internalAmountExTax = newValue;
      this.onAmountExTax.trigger();
    }

    private readonly onAmountExTax = new SimpleEvent<void>();
    public get onAmountExTaxChanged() {
      return this.onAmountExTax.expose();
    }

    private internalSelectToInvoice: boolean = false;
    public get selectToInvoice(): boolean {
      return this.internalSelectToInvoice;
    }

    public set selectToInvoice(selected: boolean) {
      this.internalSelectToInvoice = selected;
      this.onSelected.trigger();
    }

    private readonly onSelected = new SimpleEvent<void>();
    public get onSelectedChanged() {
      return this.onSelected.expose();
    }
  }

  export class LineItemDetail {}

  @Component({
    components: {
      vSelect
    },
    props: {
      jobItem: {
        type: Object as () => IJobItemModel,
        required: true
      },
      jobGid: {
        type: String,
        required: true
      },
      jobIsQuoted: Boolean,
      isInDialogMode: Boolean
    },
    filters: {
      toDate: toDateFormatter,
      toHoursMinutes: toHoursMinutesFormatter,
      toCurrency: toCurrencyFormatter
    }
  })
  export default class AddJobItemInvoice extends Vue {
    private activities: IJobItemActivityModel[] = [];
    private uninvoicedActivities: UninvoicedActivityDetail[] = [];
    private job: IJobModel | null = null;
    private jobItemGid: string = '';
    private mergeActivitiesToSingleLineItem: boolean = false;
    private invoiceTotal: number = 0;

    private currentUser: UserProfileModel | null = null;
    private jobItemServiceType: IServiceTypeModel | undefined;
    private selectedBatch: IInvoiceBatchModel | null = null;
    private availableBatches: IInvoiceBatchModel[] = [];

    private defaultClient: ClientSearchResultModel | null = null;
    private clientName: string = '';
    private selectedClient: ClientSearchResultModel | null = null;
    private matchingClients: ClientSearchResultModel[] = [];
    private suppressClientReset: boolean = false;

    private canCreateInvoice: boolean = false;

    protected mounted() {
      this.jobItemGid = this.$props.jobItem.gid;
      this.fetchJob(this.$props.jobGid);
      this.fetchServiceType(this.$props.jobItem.serviceTypeGid);
      this.fetchInvoiceBatches(undefined);
      this.fetchCurrentUser();
    }

    protected destroy() {
      // Unregiste the event handlers
      this.uninvoicedActivities.forEach(element => {
        element.onSelectedChanged.off(this.onSelectedInvoiceChanged);
        element.onAmountExTaxChanged.off(this.onAmountExTaxChanged);
        element.onAccountCodeChanged.off(this.onAccountCodeChanged);
      });
    }

    private fetchCurrentUser() {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.getUserProfile().then((data: UserProfileModel) => {
        this.currentUser = data;
      });
    }
    private fetchJob(jobGid: string) {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

      clientService.getJob(jobGid).then(data => {
        this.job = data;
        if (data.client) {
          this.clientName = data.client.name || '';
          this.defaultClient = new ClientSearchResultModel();
          this.defaultClient.init({ name: data.client.name, gid: data.client.gid });
          this.selectedClient = this.defaultClient;
        }
      });
    }

    private fetchJobActivity(jobGid: string, jobItemGid: string) {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

      clientService.getJobItemActivities(jobGid, jobItemGid).then(data => {
        this.activities = data.filter(jia => !jia.isInvoiced);
        // Convert to the UninvoicedActivityDetail model
        this.uninvoicedActivities = this.activities.map(a => this.convertActivityToUninvoicedActivity(a));
        console.log('Uninvoiced activities', this.uninvoicedActivities);
      });
    }

    private fetchServiceType(serviceTypeGid: string) {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

      clientService.getServiceType(serviceTypeGid).then(data => {
        this.jobItemServiceType = data;
        // Now fetch the job actvities
        this.fetchJobActivity(this.$props.jobGid, this.jobItemGid);
      });
    }

    private convertActivityToUninvoicedActivity(act: IJobItemActivityModel) {
      const ua = new UninvoicedActivityDetail();
      // register the event handler
      ua.onSelectedChanged.on(this.onSelectedInvoiceChanged);
      ua.onAmountExTaxChanged.on(this.onAmountExTaxChanged);
      ua.onAccountCodeChanged.on(this.onAccountCodeChanged);
      ua.gid = act.gid || '';
      ua.selectToInvoice = true;
      ua.activityDate = act.activityDateTime;
      if (this.jobItemServiceType) {
        ua.accountCode = this.jobItemServiceType.accountCode || '';
      }
      // Use the activity notes as default, then
      // fallback to the job name
      if (act.notes && act.notes !== '' ) {
        ua.description = act.notes;
      } else {
        ua.description = this.$props.jobItem.name;
      }
      if (act.employee) {
        ua.employeeName = act.employee.firstName + ' ' + act.employee.lastName;
      }
      ua.siteTimeInMinutes = act.siteTimeInMinutes || 0;
      ua.travelTimeInMinutes = act.travelTimeInMinutes || 0;
      ua.amountExTax = 0;
      return ua;
    }

    private fetchInvoiceBatches(clientGid: string | undefined) {
      let currentSelectedGid: string | undefined;
      if (this.selectedBatch) {
        currentSelectedGid = this.selectedBatch.gid;
      }

      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.getInvoiceBatches().then(data => {
        this.availableBatches = data.filter(b => !b.isClientBatch || (clientGid && b.clientGid === clientGid));

        // locate the matching batch for the previous one
        // otherwise locate the first matching client batch
        // otherwise locate the first non-client batch
        let newSelectedBatch: IInvoiceBatchModel | undefined;
        if (currentSelectedGid) {
          newSelectedBatch = this.availableBatches.find(b => b.gid === currentSelectedGid);
        }
        if (!newSelectedBatch && clientGid) {
          newSelectedBatch = this.availableBatches.find(b => b.clientGid === clientGid);
        }
        if (!newSelectedBatch) {
          newSelectedBatch = this.availableBatches.find(b => !b.isClientBatch);
        }
        if (newSelectedBatch) {
          this.selectedBatch = newSelectedBatch;
        }
        console.log('Detected default batch is', newSelectedBatch);
        this.validate();
      });
    }

    private searchForClients(search: string, loading: (show: boolean) => any) {
      if (search !== undefined && search !== null && search !== '') {
        loading(true);
        this.internalSearchForClients(search, loading, this);
      }
    }

    // Taken from: https://stackoverflow.com/questions/40686767/using-lodash-debounce-in-angular2-typescript-scroll-not-working
    private internalSearchForClients = debounce((search, loading, vm) => {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.searchClients(search).then(data => {
        // push the results into the list
        vm.matchingClients = data.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''));
        loading(false);
      });
    }, 300);

    private onSelectedInvoiceChanged() {
      this.recalculateInvoiceTotal();
      this.validate();
    }
    private onAccountCodeChanged() {
      this.validate();
    }

    private onAmountExTaxChanged() {
      this.recalculateInvoiceTotal();
      this.validate();
    }

    private recalculateInvoiceTotal() {
      const toInvoice = this.uninvoicedActivities.filter(ua => ua.selectToInvoice);
      let newTotal: number = 0;
      if (toInvoice) {
        toInvoice.forEach((element: UninvoicedActivityDetail) => {
          newTotal = (newTotal + element.amountExTax) as number;
        });
      }
      this.invoiceTotal = newTotal;
    }


    @Watch('selectedBatch')
    private onSelectedBatchChanged() {
      console.log(this.selectedBatch);
      this.validate();
    }

    @Watch('selectedClient')
    private onSelectedClientChanged() {
      if (this.selectedClient !== null) {
        // Requery for the possible batches
        this.fetchInvoiceBatches(this.selectedClient.gid);
      } else {
        // Need to reset the client to the default client
        // for the job
        if (!this.suppressClientReset) {
          console.log('Resetting default client to ', this.defaultClient);
          this.suppressClientReset = true;
          this.selectedClient = this.defaultClient;
        }
        this.suppressClientReset = false;
      }
      this.validate();
    }

    private validate() {
      let newState: boolean = true;
      // make sure there is a selected client
      if (!this.selectedClient) {
        newState = false;
      }

      // make sure there is a selected batch
      if (!this.selectedBatch) {
        newState = false;
      }
      // make sure there are selected invoices
      const selectedCount = this.uninvoicedActivities.filter(ua => ua.selectToInvoice);
      if (!selectedCount || selectedCount.length === 0) {
        newState = false;
      } else {
        // Make sure each line item has an account code
        selectedCount.forEach(element => {
          if (!element.accountCode || element.accountCode === '') {
            newState = false;
          }
        });
      }
      this.canCreateInvoice = newState;
    }
    private createInvoice() {
      const newInvoice: CreateInvoiceRequestModel = new CreateInvoiceRequestModel();
      const user: UserModel = new UserModel();
      user.init(this.currentUser);
      let theClientGid: string | undefined = undefined;
      let theBatchGid: string | undefined = undefined;
      let theJobGid: string | undefined = undefined;
      // let invoiceDate: Date = new Date();



      if (this.job) {
        theJobGid = this.job.gid || undefined;
      }
      if (this.selectedClient) {
        theClientGid = this.selectedClient.gid || undefined;
      }
      if (this.selectedBatch) {
        theBatchGid = this.selectedBatch.gid || undefined;
      }
      newInvoice.init({
        invoiceBatchGid: theBatchGid,
        clientGid: theClientGid,
        createdByUserGid: user.gid,
        jobGid: theJobGid,
        amountExTax: this.invoiceTotal,
        amountTax: this.invoiceTotal * 0.1,
        amountIncTax: this.invoiceTotal + this.invoiceTotal * 0.1,
        paid: false,
        amountPaid: 0
      });
      // Push the line items in
      const activitiesToInvoice = this.uninvoicedActivities.filter(ua => ua.selectToInvoice);
      const masterActivity = activitiesToInvoice[0];
      // Update the invoice date
      newInvoice.invoiceDate = masterActivity.activityDate;

      if (this.mergeActivitiesToSingleLineItem) {
        // Single line item only for all linked job activities
        const newLineItem: CreateInvoiceRequestLineItemModel = new CreateInvoiceRequestLineItemModel();

        newLineItem.init({
          accountCode: masterActivity.accountCode,
          amountUnit: this.invoiceTotal,
          quantity: 1,
          description: masterActivity.description,
          amountExTax: this.invoiceTotal,
          amountTax: this.invoiceTotal * 0.1,
          amountIncTax: this.invoiceTotal + this.invoiceTotal * 0.1,
          jobItemActivityGids: activitiesToInvoice.map(ati => ati.gid)
        });
        newInvoice.lineItems = [newLineItem];
      } else {
        // On invoice per item
        newInvoice.lineItems = [];
        activitiesToInvoice.forEach(element => {
          const newLineItem: CreateInvoiceRequestLineItemModel = new CreateInvoiceRequestLineItemModel();
          const lineTotal: number = element.amountExTax as number;
          newLineItem.init({
            accountCode: element.accountCode,
            amountUnit: lineTotal,
            quantity: 1,
            description: element.description,
            amountExTax: lineTotal,
            amountTax: lineTotal * 0.1,
            amountIncTax: lineTotal + lineTotal * 0.1,
            jobItemActivityGids: [element.gid]
          });
          if (newInvoice.lineItems) {
            newInvoice.lineItems.push(newLineItem);
          }
        });
      }
      console.log(newInvoice);
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      // @ts-ignore
      this.$Progress.start();
      clientService
        .createInvoice(newInvoice)
        .then((data: InvoiceModel) => {
          // Route to the edit job list screen?
          // @ts-ignore
          this.$Progress.finish();
          // @ts-ignore
          this.$awn.success('Invoice ' + data.invoiceNumber + ' created successfully');
          this.$emit('invoice-created', { jobInvoice: data, invoicedActivites: activitiesToInvoice.map((activity) => activity.gid)});
          EventBus.$emit('invoice-created', new InvoiceCreatedMessage(data));
          this.closeModal();
        })
        .catch(reason => {
          // Pop the error message
          const errMsg = 'An error occurred when creating the invoice: <br/><br/>' + reason;
          // @ts-ignore
          this.$Progress.finish();
          // @ts-ignore
          this.$awn.alert(errMsg);
        });
    }

    private cancelEdit() {
      this.closeModal();
    }

    private closeModal() {
      if (this.$props.isInDialogMode) {
        this.$modal.hide('modal-jobiteminvoice-create');
      }
    }
  }
